import { ReactNode, lazy, Suspense } from "react";
import PlateTalk from "@/pages/plate-talk-shell";
import { FirstLogCelebration } from "@/components/onboarding/first-log-celebration";
import { useTrackRouteForActivation } from "@/hooks/use-activation-status";
import { DelegationBanner } from "@/lib/delegation";
import { useLocation } from "wouter";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { isProUser } from "./nav-config";
import { Sidebar, MobileNav } from "./sidebar";
import { NotificationBell } from "@/components/notification-bell";
import { Loader2, MessageSquare, X } from "lucide-react";
import { PlateOSLogo } from "@/components/plate-os-logo";
import { usePlateTalkStore } from "@/lib/plate-talk-store";
import { AudioControlFAB } from "@/components/audio-control-fab";
import { QuickLogFAB } from "@/components/quick-log-fab";
import { GlobalPlateTalk } from "@/components/global-plate-talk";
import { useDashboardModal, closeDashboardModal } from "@/lib/dashboard-modal-store";
import { useAuth } from "@clerk/react";
import { SignupRecoveryNotice } from "@/components/signup-recovery-notice";

const DashboardPage = lazy(() => import("@/pages/dashboard"));

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [location] = useLocation();
  const { userId } = useAuth();
  const { data: user } = useGetCurrentUser();
  useTrackRouteForActivation(location);

  const hasPremiumConversation = isProUser(user);
  const hasRoleWorkspace = user?.role === "admin" || user?.role === "coach";
  if (!hasPremiumConversation && !hasRoleWorkspace) {
    return <LegacyAppLayout>{children}</LegacyAppLayout>;
  }

  return (
    <>
      <PlateTalk
        key={userId}
        ownerId={userId ?? undefined}
        conversationEnabled={hasPremiumConversation}
        directoryBanner={<DelegationBanner />}
        recoveryNotice={<SignupRecoveryNotice />}
      >
        {children}
      </PlateTalk>
      <FirstLogCelebration />
    </>
  );
}

function PlateTalkBgBanner() {
  const { state, completedWhileAway, queueLength } = usePlateTalkStore();
  const [location, setLocation] = useLocation();
  if (location === "/plate-talk") return null;

  if (state !== "idle") {
    return (
      <button
        onClick={() => setLocation("/plate-talk")}
        className="flex w-full items-center gap-2 border-b border-primary/20 bg-primary/10 px-4 py-1.5 text-xs text-primary transition-colors hover:bg-primary/15"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span className="font-medium">Plate Talk is analyzing...</span>
        {queueLength > 0 && <span className="font-medium text-primary/60">{queueLength} queued</span>}
        <span className="ml-auto text-primary/60">Tap to view</span>
      </button>
    );
  }

  if (completedWhileAway) {
    return (
      <button
        onClick={() => setLocation("/plate-talk")}
        className="flex w-full items-center gap-2 border-b border-emerald-500/20 bg-emerald-500/10 px-4 py-1.5 text-xs text-emerald-400 transition-colors hover:bg-emerald-500/15"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        <span className="font-medium">Plate Talk has a response ready</span>
        <span className="ml-auto text-emerald-400/60">Tap to view</span>
      </button>
    );
  }

  return null;
}

function DashboardModal() {
  const { isOpen } = useDashboardModal();
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 md:p-6" onClick={closeDashboardModal}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div
        className="relative z-10 flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border/60 bg-background shadow-[0_0_80px_hsl(var(--primary)/0.12)] md:h-[88vh]"
        onClick={event => event.stopPropagation()}
      >
        <button
          onClick={closeDashboardModal}
          className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-lg border border-border/40 bg-background/80 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          aria-label="Close dashboard"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto max-w-7xl p-4 pb-52 md:p-9 md:pb-12">
            <Suspense fallback={<div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}>
              <DashboardPage />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
}

function LegacyAppLayout({ children }: AppLayoutProps) {
  return (
    <div className="relative flex min-h-screen bg-background text-foreground">
      <div className="ambient-bg" aria-hidden />
      <Sidebar />
      <main className="relative z-10 h-screen flex-1 overflow-y-auto overflow-x-hidden">
        <SignupRecoveryNotice />
        <DelegationBanner />
        <div className="sticky top-0 z-[60] flex flex-col border-b border-border bg-background/95 backdrop-blur-md md:hidden">
          <div className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-2">
              <PlateOSLogo size={24} />
              <span className="text-sm font-bold tracking-tight text-foreground">Plate OS</span>
            </div>
            <NotificationBell />
          </div>
          <PlateTalkBgBanner />
        </div>
        <div className="hidden md:block"><PlateTalkBgBanner /></div>
        <div className="mx-auto max-w-7xl p-4 pb-52 md:p-9 md:pb-9">{children}</div>
      </main>
      <MobileNav />
      <QuickLogFAB />
      <AudioControlFAB />
      <GlobalPlateTalk />
      <FirstLogCelebration />
      <DashboardModal />
    </div>
  );
}
